import React, { useEffect, useState, useRef, useCallback } from 'react';
import useAuthGuard from '../hooks/useAuthGuard';
import Layout from '../components/Layout';
import { API_BASE_URL } from '../config';
import apiFetch from '../utils/apiFetch';

const MACHINE_TYPES = [
  'hook', 'bridge', 'three_claw', 'two_claw', 'pera_ring',
  'pusher', 'cutting', 'takoyaki', 'jewel_catcher', 'okashi_tower',
  'flat_catcher', 'slide', 'other',
];

const STATUS_LABELS = {
  uploading: { text: 'Uploading', color: 'info' },
  processing: { text: 'Processing', color: 'warning' },
  completed: { text: 'Completed', color: 'success' },
  partial: { text: 'Partial', color: 'secondary' },
  failed: { text: 'Failed', color: 'danger' },
};

function VideoUpload() {
  useAuthGuard();

  // Upload form state
  const [stores, setStores] = useState([]);
  const [selectedStoreId, setSelectedStoreId] = useState('');
  const [inputMode, setInputMode] = useState('file'); // 'file' or 'url'
  const [videoFile, setVideoFile] = useState(null);
  const [videoUrl, setVideoUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState('');
  const fileInputRef = useRef(null);

  // Session list state
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [expandedSession, setExpandedSession] = useState(null);
  const [sessionRecords, setSessionRecords] = useState({});
  const [sessionJobs, setSessionJobs] = useState({});
  const [sessionSortBy, setSessionSortBy] = useState('time_desc');
  const [filterStoreId, setFilterStoreId] = useState('');

  // Image preview modal state: index-based for navigation
  const [previewSessionId, setPreviewSessionId] = useState(null);
  const [previewIndex, setPreviewIndex] = useState(0);

  // Inline editing state for preview modal
  const [editing, setEditing] = useState(false);
  const [editMachineType, setEditMachineType] = useState('');
  const [editPrizeName, setEditPrizeName] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  // Load stores on mount
  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      try {
        const res = await apiFetch(`${API_BASE_URL}/admin/stores?limit=500`, { signal: ac.signal });
        if (res.ok) {
          const data = await res.json();
          setStores(Array.isArray(data.data) ? data.data : []);
        }
      } catch (e) {
        if (e.name !== 'AbortError') console.error('Failed to load stores:', e);
      }
    })();
    return () => ac.abort();
  }, []);

  // Load sessions on mount and after upload
  const loadSessions = useCallback(async () => {
    setSessionsLoading(true);
    try {
      let url = `${API_BASE_URL}/admin/scan-sessions?limit=50&session_type=video`;
      if (filterStoreId) url += `&store_id=${filterStoreId}`;
      const res = await apiFetch(url);
      if (res.ok) {
        const data = await res.json();
        setSessions(Array.isArray(data.data) ? data.data : []);
      }
    } catch (e) {
      console.error('Failed to load sessions:', e);
    } finally {
      setSessionsLoading(false);
    }
  }, [filterStoreId]);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  // Auto-refresh every 5s if any session is processing
  useEffect(() => {
    const hasProcessing = sessions.some((s) => s.status === 'processing' || s.status === 'uploading');
    if (!hasProcessing) return;
    const interval = setInterval(loadSessions, 5000);
    return () => clearInterval(interval);
  }, [sessions, loadSessions]);

  // Upload handler
  const handleUpload = async () => {
    setUploadError('');
    setUploadSuccess('');

    if (inputMode === 'file' && !videoFile) {
      setUploadError('Please select a video file');
      return;
    }
    if (inputMode === 'url' && !videoUrl.trim()) {
      setUploadError('Please enter a video URL');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      // Step 1: Create session
      const sessionRes = await apiFetch(`${API_BASE_URL}/admin/scan-sessions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionType: 'video',
          totalImages: 0,
          ...(selectedStoreId ? { storeId: selectedStoreId } : {}),
        }),
      });

      if (!sessionRes.ok) throw new Error(`Session creation failed: ${sessionRes.status}`);
      const sessionData = await sessionRes.json();
      const sessionId = sessionData.data?.id;
      if (!sessionId) throw new Error('No session ID returned');

      if (inputMode === 'file') {
        // Step 2a: Get presigned URL
        const urlRes = await apiFetch(`${API_BASE_URL}/admin/upload/video-url?content_type=video/mp4`);
        if (!urlRes.ok) throw new Error(`Failed to get upload URL: ${urlRes.status}`);
        const urlData = await urlRes.json();
        const presignedUrl = urlData.data?.uploadUrl;
        const publicUrl = urlData.data?.publicUrl;

        // Step 2b: Upload to S3 with progress
        await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.upload.addEventListener('progress', (e) => {
            if (e.lengthComputable) {
              setUploadProgress(Math.round((e.loaded / e.total) * 100));
            }
          });
          xhr.addEventListener('load', () => {
            if (xhr.status === 200) resolve();
            else reject(new Error(`S3 upload failed: ${xhr.status}`));
          });
          xhr.addEventListener('error', () => reject(new Error('Upload network error')));
          xhr.open('PUT', presignedUrl);
          xhr.setRequestHeader('Content-Type', 'video/mp4');
          xhr.send(videoFile);
        });

        // Step 2c: Notify server
        const notifyRes = await apiFetch(`${API_BASE_URL}/admin/scan-sessions/${sessionId}/video`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ videoUrl: publicUrl, filename: videoFile.name }),
        });
        if (!notifyRes.ok) throw new Error(`Video notification failed: ${notifyRes.status}`);

      } else {
        // URL mode: directly notify
        const notifyRes = await apiFetch(`${API_BASE_URL}/admin/scan-sessions/${sessionId}/video`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ videoUrl: videoUrl.trim() }),
        });
        if (!notifyRes.ok) throw new Error(`Video notification failed: ${notifyRes.status}`);
      }

      setUploadSuccess('Video uploaded successfully. Frame extraction started.');
      setVideoFile(null);
      setVideoUrl('');
      setUploadProgress(0);
      if (fileInputRef.current) fileInputRef.current.value = '';

      // Refresh sessions list
      loadSessions();
    } catch (e) {
      setUploadError(e.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  // Expand session to see scan records + job details
  const toggleSession = async (sessionId) => {
    if (expandedSession === sessionId) {
      setExpandedSession(null);
      return;
    }
    setExpandedSession(sessionId);
    loadSessionDetail(sessionId);
  };

  const loadSessionDetail = async (sessionId) => {
    try {
      const [recRes, jobRes] = await Promise.all([
        apiFetch(`${API_BASE_URL}/admin/scans?session_id=${sessionId}&limit=200&sort_by=${sessionSortBy}`),
        apiFetch(`${API_BASE_URL}/admin/scan-sessions/${sessionId}/jobs`),
      ]);
      if (recRes.ok) {
        const data = await recRes.json();
        setSessionRecords((prev) => ({
          ...prev,
          [sessionId]: Array.isArray(data.data) ? data.data : [],
        }));
      }
      if (jobRes.ok) {
        const data = await jobRes.json();
        setSessionJobs((prev) => ({
          ...prev,
          [sessionId]: Array.isArray(data.data) ? data.data : [],
        }));
      }
    } catch (e) {
      console.error('Failed to load session details:', e);
    }
  };

  // Auto-refresh expanded session detail
  useEffect(() => {
    if (!expandedSession) return;
    const session = sessions.find((s) => s.id === expandedSession);
    if (!session || session.status === 'completed' || session.status === 'failed' || session.status === 'partial') return;
    const interval = setInterval(() => loadSessionDetail(expandedSession), 5000);
    return () => clearInterval(interval);
  }, [expandedSession, sessions]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reload session detail when sort changes
  useEffect(() => {
    if (expandedSession) loadSessionDetail(expandedSession);
  }, [sessionSortBy]); // eslint-disable-line react-hooks/exhaustive-deps

  // Delete session handler
  const handleDeleteSession = async (sessionId, e) => {
    e.stopPropagation();
    if (!window.confirm('このセッションと関連するすべてのデータを削除しますか？')) return;
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/scan-sessions/${sessionId}`, {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error(`Delete failed: ${res.status}`);
      loadSessions();
      if (expandedSession === sessionId) setExpandedSession(null);
    } catch (e2) {
      alert('削除に失敗しました: ' + e2.message);
    }
  };

  // Retry session handler
  const handleRetrySession = async (sessionId, e) => {
    e.stopPropagation();
    if (!window.confirm('このセッションを再処理しますか？')) return;
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/scan-sessions/${sessionId}/retry`, {
        method: 'POST',
      });
      if (!res.ok) throw new Error(`Retry failed: ${res.status}`);
      loadSessions();
    } catch (e2) {
      alert('リトライに失敗しました: ' + e2.message);
    }
  };

  // Update session store handler
  const handleUpdateSessionStore = async (sessionId, newStoreId) => {
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/scan-sessions/${sessionId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ storeId: newStoreId || null }),
      });
      if (!res.ok) throw new Error(`Update failed: ${res.status}`);
      loadSessions();
    } catch (e2) {
      alert('Store変更に失敗しました: ' + e2.message);
    }
  };

  // Preview modal helpers
  const previewRecords = previewSessionId ? (sessionRecords[previewSessionId] || []) : [];
  const previewRecord = previewRecords[previewIndex] || null;

  const openPreview = (sessionId, index) => {
    setPreviewSessionId(sessionId);
    setPreviewIndex(index);
  };

  const closePreview = () => {
    setPreviewSessionId(null);
    setPreviewIndex(0);
    setEditing(false);
  };

  const prevPreview = () => {
    setEditing(false);
    setPreviewIndex((i) => (i > 0 ? i - 1 : previewRecords.length - 1));
  };

  const nextPreview = () => {
    setEditing(false);
    setPreviewIndex((i) => (i < previewRecords.length - 1 ? i + 1 : 0));
  };

  const startEdit = () => {
    if (!previewRecord) return;
    setEditMachineType(previewRecord.correctedMachineType || previewRecord.machineType || '');
    setEditPrizeName(previewRecord.correctedPrizeName || previewRecord.prizeName || '');
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
  };

  const saveEdit = async () => {
    if (!previewRecord) return;
    setEditSaving(true);
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/scans/${previewRecord.id}/review`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: 'confirmed',
          machineType: editMachineType,
          prizeName: editPrizeName,
        }),
      });
      if (!res.ok) throw new Error(`Review failed: ${res.status}`);
      const data = await res.json();
      // Update the record in sessionRecords
      if (previewSessionId) {
        setSessionRecords((prev) => {
          const records = [...(prev[previewSessionId] || [])];
          records[previewIndex] = data.data || data;
          return { ...prev, [previewSessionId]: records };
        });
      }
      setEditing(false);
    } catch (e) {
      alert('Save failed: ' + e.message);
    } finally {
      setEditSaving(false);
    }
  };

  // Keyboard navigation for preview modal
  useEffect(() => {
    if (!previewSessionId) return;
    const handler = (e) => {
      if (e.key === 'ArrowLeft') prevPreview();
      else if (e.key === 'ArrowRight') nextPreview();
      else if (e.key === 'Escape') closePreview();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }); // intentionally no deps - uses latest closures

  return (
    <Layout>
      <div className="mb-4">
        <h2 className="page-title">Video Scan</h2>
        <p className="page-subtitle">Upload store walkthrough videos for AI analysis</p>
      </div>

      {/* Upload Form */}
      <div className="card mb-4">
        <div className="card-header bg-white py-2">
          <h6 className="mb-0" style={{ fontSize: '0.875rem' }}>Upload Video</h6>
        </div>
        <div className="card-body">
          {/* Store selector */}
          <div className="mb-3">
            <label className="form-label small fw-medium">Store (optional)</label>
            <select
              className="form-select form-select-sm"
              value={selectedStoreId}
              onChange={(e) => setSelectedStoreId(e.target.value)}
            >
              <option value="">-- Select store --</option>
              {stores.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>

          {/* Input mode tabs */}
          <div className="mb-3">
            <ul className="nav nav-tabs nav-tabs-sm">
              <li className="nav-item">
                <button
                  className={`nav-link small ${inputMode === 'file' ? 'active' : ''}`}
                  onClick={() => setInputMode('file')}
                >
                  File Upload
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link small ${inputMode === 'url' ? 'active' : ''}`}
                  onClick={() => setInputMode('url')}
                >
                  URL Input
                </button>
              </li>
            </ul>
          </div>

          {inputMode === 'file' ? (
            <div className="mb-3">
              <input
                ref={fileInputRef}
                type="file"
                className="form-control form-control-sm"
                accept="video/mp4,video/quicktime"
                onChange={(e) => setVideoFile(e.target.files?.[0] || null)}
              />
              <small className="text-muted">MP4 or MOV, max 100MB</small>
            </div>
          ) : (
            <div className="mb-3">
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="https://... (S3 or CloudFront URL)"
                value={videoUrl}
                onChange={(e) => setVideoUrl(e.target.value)}
              />
              <small className="text-muted">Enter a direct URL to the video file</small>
            </div>
          )}

          {/* Progress bar */}
          {uploading && inputMode === 'file' && (
            <div className="mb-3">
              <div className="progress" style={{ height: 6 }}>
                <div
                  className="progress-bar bg-primary"
                  role="progressbar"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <small className="text-muted">{uploadProgress}%</small>
            </div>
          )}

          {uploadError && (
            <div className="alert alert-danger py-2 small mb-3">{uploadError}</div>
          )}
          {uploadSuccess && (
            <div className="alert alert-success py-2 small mb-3">{uploadSuccess}</div>
          )}

          <button
            className="btn btn-primary btn-sm"
            disabled={uploading}
            onClick={handleUpload}
          >
            {uploading ? (
              <>
                <span className="spinner-border spinner-border-sm me-1" />
                Uploading...
              </>
            ) : (
              'Upload & Analyze'
            )}
          </button>
        </div>
      </div>

      {/* Session List */}
      <div className="card">
        <div className="card-header bg-white py-2 d-flex justify-content-between align-items-center">
          <h6 className="mb-0" style={{ fontSize: '0.875rem' }}>Video Sessions</h6>
          <div className="d-flex align-items-center gap-2">
            <select
              className="form-select form-select-sm"
              style={{ width: 'auto', fontSize: '0.75rem', minWidth: 140 }}
              value={filterStoreId}
              onChange={(e) => setFilterStoreId(e.target.value)}
            >
              <option value="">All Stores</option>
              {stores.map((st) => (
                <option key={st.id} value={st.id}>{st.name}</option>
              ))}
            </select>
            <button className="btn btn-outline-secondary btn-sm" onClick={loadSessions} disabled={sessionsLoading}>
              {sessionsLoading ? <span className="spinner-border spinner-border-sm" /> : 'Refresh'}
            </button>
          </div>
        </div>
        <div className="card-body p-0">
          {sessions.length === 0 && !sessionsLoading ? (
            <p className="text-center py-4 text-muted small">No video sessions yet</p>
          ) : (
            <table className="table table-sm mb-0">
              <thead>
                <tr>
                  <th className="ps-3 small" style={{ color: '#8a94ad' }}>File</th>
                  <th className="small" style={{ color: '#8a94ad' }}>Store</th>
                  <th className="small" style={{ color: '#8a94ad' }}>Status</th>
                  <th className="small" style={{ color: '#8a94ad' }}>Frames</th>
                  <th className="small" style={{ color: '#8a94ad' }}>Created</th>
                  <th className="small" style={{ color: '#8a94ad' }}>Last Updated</th>
                  <th className="small" style={{ color: '#8a94ad' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((s) => {
                  const status = STATUS_LABELS[s.status] || { text: s.status, color: 'secondary' };
                  const isExpanded = expandedSession === s.id;
                  return (
                    <React.Fragment key={s.id}>
                      <tr
                        style={{ cursor: 'pointer' }}
                        onClick={() => toggleSession(s.id)}
                        className={isExpanded ? 'table-active' : ''}
                      >
                        <td className="ps-3 small fw-medium" style={{ maxWidth: 200 }}>
                          {isExpanded ? '\u25BC' : '\u25B6'}{' '}
                          {s.originalFilename || `${s.id?.substring(0, 8)}...`}
                        </td>
                        <td className="small" onClick={(e) => e.stopPropagation()} style={{ minWidth: 140 }}>
                          <select
                            className="form-select form-select-sm py-0"
                            style={{ fontSize: '0.75rem', width: 'auto', minWidth: 120 }}
                            value={s.storeId || ''}
                            onChange={(e) => handleUpdateSessionStore(s.id, e.target.value)}
                          >
                            <option value="">-- None --</option>
                            {stores.map((st) => (
                              <option key={st.id} value={st.id}>{st.name}</option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <span className={`badge bg-${status.color}`} style={{ fontSize: '0.7rem' }}>
                            {status.text}
                          </span>
                          {s.status === 'processing' && (
                            <span className="spinner-border spinner-border-sm ms-1" style={{ width: 10, height: 10 }} />
                          )}
                        </td>
                        <td className="small">
                          {s.processedImages}/{s.totalImages}
                          {s.failedImages > 0 && (
                            <span className="text-danger ms-1">({s.failedImages} failed)</span>
                          )}
                        </td>
                        <td className="small" style={{ color: '#8a94ad' }}>
                          {s.createdAt ? new Date(s.createdAt).toLocaleString('ja-JP') : '-'}
                        </td>
                        <td className="small" style={{ color: '#8a94ad' }}>
                          {s.updatedAt ? new Date(s.updatedAt).toLocaleString('ja-JP') : '-'}
                        </td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <div className="d-flex gap-1">
                            {(s.status === 'failed' || s.status === 'partial' || (s.status === 'processing' && s.processedImages === 0 && s.totalImages === 0)) && (
                              <button
                                className="btn btn-outline-primary btn-sm py-0 px-1"
                                style={{ fontSize: '0.65rem' }}
                                onClick={(e) => handleRetrySession(s.id, e)}
                                title="Retry"
                              >
                                Retry
                              </button>
                            )}
                            <button
                              className="btn btn-outline-danger btn-sm py-0 px-1"
                              style={{ fontSize: '0.65rem' }}
                              onClick={(e) => handleDeleteSession(s.id, e)}
                              title="Delete"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td colSpan={7} className="px-3 py-2" style={{ background: '#f8f9fa' }}>
                            {/* Job Pipeline Status */}
                            {sessionJobs[s.id] && sessionJobs[s.id].length > 0 && (
                              <div className="mb-3">
                                <h6 className="small fw-bold mb-2">Job Pipeline</h6>
                                {(() => {
                                  const jobs = sessionJobs[s.id];
                                  const clipJob = jobs.find((j) => j.jobType === 'youtube_clip_extraction');
                                  const extractionJob = jobs.find((j) => j.jobType === 'video_frame_extraction');
                                  const analysisJobs = jobs.filter((j) => j.jobType === 'image_analysis');
                                  const analysisDone = analysisJobs.filter((j) => j.status === 'completed').length;
                                  const analysisFailed = analysisJobs.filter((j) => j.status === 'failed').length;
                                  const analysisProcessing = analysisJobs.filter((j) => j.status === 'processing').length;
                                  const analysisPending = analysisJobs.filter((j) => j.status === 'pending').length;
                                  const hasClipStep = !!clipJob;
                                  return (
                                    <div className="d-flex align-items-start gap-3 flex-wrap">
                                      {/* Step: YouTube Clip Extraction (only for YouTube curation sessions) */}
                                      {hasClipStep && (
                                        <>
                                          <div className="border rounded p-2" style={{ minWidth: 220, background: '#fff' }}>
                                            <div className="d-flex justify-content-between align-items-center mb-1">
                                              <small className="fw-bold">1. Clip Download</small>
                                              <span className={`badge bg-${clipJob.status === 'completed' ? 'success' : clipJob.status === 'failed' ? 'danger' : clipJob.status === 'processing' ? 'primary' : 'warning'}`} style={{ fontSize: '0.65rem' }}>
                                                {clipJob.status}
                                              </span>
                                            </div>
                                            <small className="d-block text-muted" style={{ fontSize: '0.7rem' }}>
                                              Attempts: {clipJob.attempts}/{clipJob.maxAttempts}
                                            </small>
                                            {clipJob.lockedBy && (
                                              <small className="d-block text-muted" style={{ fontSize: '0.7rem' }}>
                                                Worker: {clipJob.lockedBy}
                                              </small>
                                            )}
                                            {clipJob.error && (
                                              <div className="alert alert-danger py-1 px-2 mt-1 mb-0" style={{ fontSize: '0.65rem' }}>
                                                {clipJob.error}
                                              </div>
                                            )}
                                            <small className="d-block text-muted mt-1" style={{ fontSize: '0.65rem' }}>
                                              Updated: {new Date(clipJob.updatedAt).toLocaleString('ja-JP')}
                                            </small>
                                          </div>
                                          <div className="d-flex align-items-center" style={{ fontSize: 20, color: '#8a94ad', paddingTop: 12 }}>→</div>
                                        </>
                                      )}
                                      {/* Step: Frame Extraction */}
                                      <div className="border rounded p-2" style={{ minWidth: 220, background: '#fff' }}>
                                        <div className="d-flex justify-content-between align-items-center mb-1">
                                          <small className="fw-bold">{hasClipStep ? 2 : 1}. Frame Extraction</small>
                                          {extractionJob ? (
                                            <span className={`badge bg-${extractionJob.status === 'completed' ? 'success' : extractionJob.status === 'failed' ? 'danger' : extractionJob.status === 'processing' ? 'primary' : 'warning'}`} style={{ fontSize: '0.65rem' }}>
                                              {extractionJob.status}
                                            </span>
                                          ) : (
                                            <span className="badge bg-secondary" style={{ fontSize: '0.65rem' }}>
                                              {hasClipStep && clipJob.status !== 'completed' ? 'waiting' : 'no job'}
                                            </span>
                                          )}
                                        </div>
                                        {extractionJob && (
                                          <>
                                            <small className="d-block text-muted" style={{ fontSize: '0.7rem' }}>
                                              Attempts: {extractionJob.attempts}/{extractionJob.maxAttempts}
                                            </small>
                                            {extractionJob.lockedBy && (
                                              <small className="d-block text-muted" style={{ fontSize: '0.7rem' }}>
                                                Worker: {extractionJob.lockedBy}
                                              </small>
                                            )}
                                            {extractionJob.error && (
                                              <div className="alert alert-danger py-1 px-2 mt-1 mb-0" style={{ fontSize: '0.65rem' }}>
                                                {extractionJob.error}
                                              </div>
                                            )}
                                            <small className="d-block text-muted mt-1" style={{ fontSize: '0.65rem' }}>
                                              Updated: {new Date(extractionJob.updatedAt).toLocaleString('ja-JP')}
                                            </small>
                                          </>
                                        )}
                                        {!extractionJob && hasClipStep && clipJob.status !== 'completed' && (
                                          <small className="text-muted" style={{ fontSize: '0.7rem' }}>
                                            Waiting for clip download...
                                          </small>
                                        )}
                                      </div>

                                      {/* Arrow */}
                                      <div className="d-flex align-items-center" style={{ fontSize: 20, color: '#8a94ad', paddingTop: 12 }}>→</div>

                                      {/* Step: AI Analysis */}
                                      <div className="border rounded p-2" style={{ minWidth: 220, background: '#fff' }}>
                                        <div className="d-flex justify-content-between align-items-center mb-1">
                                          <small className="fw-bold">{hasClipStep ? 3 : 2}. AI Analysis</small>
                                          <small className="text-muted" style={{ fontSize: '0.7rem' }}>
                                            {analysisJobs.length} jobs
                                          </small>
                                        </div>
                                        {analysisJobs.length > 0 ? (
                                          <>
                                            <div className="progress mb-1" style={{ height: 6 }}>
                                              <div className="progress-bar bg-success" style={{ width: `${(analysisDone / analysisJobs.length) * 100}%` }} />
                                              <div className="progress-bar bg-primary" style={{ width: `${(analysisProcessing / analysisJobs.length) * 100}%` }} />
                                              <div className="progress-bar bg-danger" style={{ width: `${(analysisFailed / analysisJobs.length) * 100}%` }} />
                                            </div>
                                            <small className="d-block" style={{ fontSize: '0.7rem' }}>
                                              <span className="text-success">{analysisDone} done</span>
                                              {analysisProcessing > 0 && <span className="text-primary ms-2">{analysisProcessing} running</span>}
                                              {analysisPending > 0 && <span className="text-muted ms-2">{analysisPending} queued</span>}
                                              {analysisFailed > 0 && <span className="text-danger ms-2">{analysisFailed} failed</span>}
                                            </small>
                                            {analysisFailed > 0 && (() => {
                                              const firstError = analysisJobs.find((j) => j.status === 'failed' && j.error);
                                              return firstError ? (
                                                <div className="alert alert-danger py-1 px-2 mt-1 mb-0" style={{ fontSize: '0.65rem' }}>
                                                  {firstError.error}
                                                </div>
                                              ) : null;
                                            })()}
                                          </>
                                        ) : (
                                          <small className="text-muted" style={{ fontSize: '0.7rem' }}>
                                            Waiting for frame extraction...
                                          </small>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })()}
                              </div>
                            )}
                            {sessionJobs[s.id] && sessionJobs[s.id].length === 0 && (
                              <div className="alert alert-warning py-1 px-2 small mb-3">
                                No AI jobs found for this session. The video upload notification may have failed.
                              </div>
                            )}

                            {/* Frame Thumbnails */}
                            {!sessionRecords[s.id] ? (
                              <div className="text-center py-2">
                                <span className="spinner-border spinner-border-sm" />
                              </div>
                            ) : sessionRecords[s.id].length === 0 ? (
                              <p className="text-muted small mb-0">No frames extracted yet</p>
                            ) : (
                              <>
                                <div className="d-flex align-items-center gap-3 mb-2">
                                  <select
                                    className="form-select form-select-sm"
                                    style={{ width: 'auto', fontSize: '0.75rem' }}
                                    value={sessionSortBy}
                                    onChange={(e) => setSessionSortBy(e.target.value)}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <option value="time_desc">新しい順</option>
                                    <option value="time_asc">古い順</option>
                                    <option value="confidence_desc">信頼度 高→低</option>
                                    <option value="confidence_asc">信頼度 低→高</option>
                                  </select>
                                  <small className="text-muted" style={{ fontSize: '0.7rem' }}>
                                    {sessionRecords[s.id].length} frames
                                    {s.totalExtracted > 0 && ` / ${s.totalExtracted} total`}
                                    {s.deferredCount > 0 && ` (${s.deferredCount} deferred)`}
                                  </small>
                                </div>
                                <div className="d-flex flex-wrap gap-2">
                                {sessionRecords[s.id].map((rec, idx) => (
                                  <div key={rec.id} className="text-center" style={{ width: 120 }}>
                                    {rec.imageUrl ? (
                                      <img
                                        src={rec.imageUrl}
                                        alt="frame"
                                        style={{ width: 120, height: 130, objectFit: 'contain', borderRadius: 4, cursor: 'pointer', background: '#f0f0f0' }}
                                        onClick={(e) => { e.stopPropagation(); openPreview(s.id, idx); }}
                                      />
                                    ) : (
                                      <div style={{ width: 120, height: 130, background: '#dee2e6', borderRadius: 4 }} />
                                    )}
                                    {rec.frameIndex != null && (
                                      <small className="d-block" style={{ fontSize: '0.6rem', color: '#888', marginTop: 1 }}>
                                        {(() => {
                                          const te = s.totalExtracted || 0;
                                          const dur = s.videoDurationSec || 0;
                                          if (te > 1 && dur > 0) {
                                            const t = (rec.frameIndex / (te - 1)) * dur;
                                            const m = Math.floor(t / 60);
                                            const sc = Math.floor(t % 60);
                                            return `#${rec.frameIndex} (${m}:${sc.toString().padStart(2, '0')})`;
                                          }
                                          return `#${rec.frameIndex}`;
                                        })()}
                                      </small>
                                    )}
                                    <small
                                      className="d-block fw-medium"
                                      style={{ fontSize: '0.6rem', lineHeight: 1.2, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                      title={rec.machineType || '-'}
                                    >
                                      {rec.machineType || '-'}
                                    </small>
                                    <small
                                      className="d-block"
                                      style={{ fontSize: '0.6rem', lineHeight: 1.2, color: '#555', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                      title={rec.prizeName || '-'}
                                    >
                                      {rec.prizeName || '-'}
                                    </small>
                                    <span
                                      className={`badge bg-${rec.status === 'completed' ? 'success' : rec.status === 'failed' ? 'danger' : rec.status === 'deferred_budget' ? 'info' : 'warning'}`}
                                      style={{ fontSize: '0.6rem' }}
                                    >
                                      {rec.status === 'deferred_budget' ? 'deferred' : rec.status}
                                    </span>
                                  </div>
                                ))}
                                </div>
                              </>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Enhanced Image Preview Modal */}
      {previewRecord && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.85)', zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          onClick={closePreview}
        >
          <div
            style={{
              background: '#fff', borderRadius: 12, maxWidth: 600, width: '95vw',
              maxHeight: '95vh', overflow: 'auto', position: 'relative',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Image area with nav arrows */}
            <div style={{ position: 'relative', background: '#f0f0f0', borderRadius: '12px 12px 0 0' }}>
              {previewRecord.imageUrl ? (
                <img
                  src={previewRecord.imageUrl}
                  alt="preview"
                  style={{ width: '100%', maxHeight: '70vh', objectFit: 'contain', display: 'block', borderRadius: '12px 12px 0 0' }}
                />
              ) : (
                <div style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999' }}>
                  No image
                </div>
              )}

              {/* Left arrow */}
              {previewRecords.length > 1 && (
                <button
                  style={{
                    position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)',
                    background: 'rgba(0,0,0,0.5)', color: '#fff', border: 'none', borderRadius: '50%',
                    width: 36, height: 36, fontSize: 18, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                  onClick={prevPreview}
                >
                  &#8249;
                </button>
              )}

              {/* Right arrow */}
              {previewRecords.length > 1 && (
                <button
                  style={{
                    position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                    background: 'rgba(0,0,0,0.5)', color: '#fff', border: 'none', borderRadius: '50%',
                    width: 36, height: 36, fontSize: 18, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                  onClick={nextPreview}
                >
                  &#8250;
                </button>
              )}

              {/* Close button */}
              <button
                style={{
                  position: 'absolute', top: 8, right: 8,
                  background: 'rgba(0,0,0,0.5)', color: '#fff', border: 'none', borderRadius: '50%',
                  width: 32, height: 32, fontSize: 16, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
                onClick={closePreview}
              >
                &#x2715;
              </button>
            </div>

            {/* Metadata area */}
            <div style={{ padding: '12px 16px', fontSize: '0.8rem', lineHeight: 1.6 }}>
              {/* Edit / Save / Cancel buttons */}
              <div className="d-flex gap-2 mb-2">
                {!editing ? (
                  <button className="btn btn-outline-primary btn-sm py-0 px-2" style={{ fontSize: '0.75rem' }} onClick={startEdit}>
                    Edit
                  </button>
                ) : (
                  <>
                    <button className="btn btn-primary btn-sm py-0 px-2" style={{ fontSize: '0.75rem' }} onClick={saveEdit} disabled={editSaving}>
                      {editSaving ? 'Saving...' : 'Save'}
                    </button>
                    <button className="btn btn-outline-secondary btn-sm py-0 px-2" style={{ fontSize: '0.75rem' }} onClick={cancelEdit} disabled={editSaving}>
                      Cancel
                    </button>
                  </>
                )}
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  {previewRecord.frameIndex != null && (
                    <tr>
                      <td style={{ color: '#888', paddingRight: 12, whiteSpace: 'nowrap', verticalAlign: 'top' }}>Frame</td>
                      <td style={{ fontWeight: 500 }}>#{previewRecord.frameIndex}</td>
                    </tr>
                  )}
                  <tr>
                    <td style={{ color: '#888', paddingRight: 12, whiteSpace: 'nowrap', verticalAlign: 'top' }}>Machine Type</td>
                    <td style={{ fontWeight: 500 }}>
                      {editing ? (
                        <select
                          className="form-select form-select-sm"
                          style={{ fontSize: '0.75rem', width: 'auto' }}
                          value={editMachineType}
                          onChange={(e) => setEditMachineType(e.target.value)}
                        >
                          <option value="">-- Select --</option>
                          {MACHINE_TYPES.map((mt) => (
                            <option key={mt} value={mt}>{mt}</option>
                          ))}
                        </select>
                      ) : (
                        <>
                          {previewRecord.correctedMachineType ? (
                            <>
                              {previewRecord.correctedMachineType}
                              <span className="badge bg-info ms-1" style={{ fontSize: '0.6rem' }}>corrected</span>
                              {previewRecord.machineType && previewRecord.machineType !== previewRecord.correctedMachineType && (
                                <span style={{ color: '#aaa', fontWeight: 400, textDecoration: 'line-through', marginLeft: 6 }}>{previewRecord.machineType}</span>
                              )}
                            </>
                          ) : (
                            <>
                              {previewRecord.machineType || '-'}
                              {previewRecord.machineConfidence > 0 && (
                                <span style={{ color: '#888', fontWeight: 400 }}> ({Math.round(previewRecord.machineConfidence * 100)}%)</span>
                              )}
                            </>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td style={{ color: '#888', paddingRight: 12, whiteSpace: 'nowrap', verticalAlign: 'top' }}>Prize Name</td>
                    <td style={{ fontWeight: 500 }}>
                      {editing ? (
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          style={{ fontSize: '0.75rem' }}
                          value={editPrizeName}
                          onChange={(e) => setEditPrizeName(e.target.value)}
                        />
                      ) : (
                        <>
                          {previewRecord.correctedPrizeName ? (
                            <>
                              {previewRecord.correctedPrizeName}
                              <span className="badge bg-info ms-1" style={{ fontSize: '0.6rem' }}>corrected</span>
                              {previewRecord.prizeName && previewRecord.prizeName !== previewRecord.correctedPrizeName && (
                                <span style={{ color: '#aaa', fontWeight: 400, textDecoration: 'line-through', marginLeft: 6, fontSize: '0.7rem' }}>{previewRecord.prizeName}</span>
                              )}
                            </>
                          ) : (
                            previewRecord.prizeName || '-'
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                  {previewRecord.prizeSeriesName && (
                    <tr>
                      <td style={{ color: '#888', paddingRight: 12, whiteSpace: 'nowrap', verticalAlign: 'top' }}>Series</td>
                      <td>{previewRecord.prizeSeriesName}</td>
                    </tr>
                  )}
                  {previewRecord.prizeManufacturer && (
                    <tr>
                      <td style={{ color: '#888', paddingRight: 12, whiteSpace: 'nowrap', verticalAlign: 'top' }}>Manufacturer</td>
                      <td>{previewRecord.prizeManufacturer}</td>
                    </tr>
                  )}
                  {previewRecord.prizeCategory && (
                    <tr>
                      <td style={{ color: '#888', paddingRight: 12, whiteSpace: 'nowrap', verticalAlign: 'top' }}>Category</td>
                      <td>{previewRecord.prizeCategory}</td>
                    </tr>
                  )}
                  {previewRecord.prizeConfidence > 0 && (
                    <tr>
                      <td style={{ color: '#888', paddingRight: 12, whiteSpace: 'nowrap', verticalAlign: 'top' }}>Prize Confidence</td>
                      <td>{Math.round(previewRecord.prizeConfidence * 100)}%</td>
                    </tr>
                  )}
                  {(previewRecord.estimatedCostMin != null || previewRecord.estimatedCostMax != null) && (
                    <tr>
                      <td style={{ color: '#888', paddingRight: 12, whiteSpace: 'nowrap', verticalAlign: 'top' }}>Est. Cost</td>
                      <td>
                        {previewRecord.estimatedCostMin != null ? `\u00A5${previewRecord.estimatedCostMin.toLocaleString()}` : '?'}
                        {' ~ '}
                        {previewRecord.estimatedCostMax != null ? `\u00A5${previewRecord.estimatedCostMax.toLocaleString()}` : '?'}
                      </td>
                    </tr>
                  )}
                  <tr>
                    <td style={{ color: '#888', paddingRight: 12, whiteSpace: 'nowrap', verticalAlign: 'top' }}>Status</td>
                    <td>
                      <span className={`badge bg-${previewRecord.status === 'completed' ? 'success' : previewRecord.status === 'failed' ? 'danger' : previewRecord.status === 'deferred_budget' ? 'info' : 'warning'}`} style={{ fontSize: '0.7rem' }}>
                        {previewRecord.status === 'deferred_budget' ? 'deferred' : previewRecord.status}
                      </span>
                    </td>
                  </tr>
                  {previewRecord.reviewStatus && previewRecord.reviewStatus !== 'none' && (
                    <tr>
                      <td style={{ color: '#888', paddingRight: 12, whiteSpace: 'nowrap', verticalAlign: 'top' }}>Review</td>
                      <td>
                        <span className={`badge bg-${previewRecord.reviewStatus === 'confirmed' || previewRecord.reviewStatus === 'auto_confirmed' ? 'success' : previewRecord.reviewStatus === 'rejected' ? 'danger' : 'warning'}`} style={{ fontSize: '0.7rem' }}>
                          {previewRecord.reviewStatus}
                        </span>
                      </td>
                    </tr>
                  )}
                  {(previewRecord.aiProvider || previewRecord.aiModel) && (
                    <tr>
                      <td style={{ color: '#888', paddingRight: 12, whiteSpace: 'nowrap', verticalAlign: 'top' }}>AI</td>
                      <td style={{ fontSize: '0.75rem', wordBreak: 'break-all' }}>
                        {[previewRecord.aiProvider, previewRecord.aiModel].filter(Boolean).join(' / ')}
                      </td>
                    </tr>
                  )}
                  <tr>
                    <td style={{ color: '#888', paddingRight: 12, whiteSpace: 'nowrap', verticalAlign: 'top' }}>Scanned</td>
                    <td>{previewRecord.scannedAt ? new Date(previewRecord.scannedAt).toLocaleString('ja-JP') : '-'}</td>
                  </tr>
                  <tr>
                    <td style={{ color: '#888', paddingRight: 12, whiteSpace: 'nowrap', verticalAlign: 'top' }}>Created</td>
                    <td>{previewRecord.createdAt ? new Date(previewRecord.createdAt).toLocaleString('ja-JP') : '-'}</td>
                  </tr>
                  {(previewRecord.exifLatitude != null && previewRecord.exifLongitude != null) && (
                    <tr>
                      <td style={{ color: '#888', paddingRight: 12, whiteSpace: 'nowrap', verticalAlign: 'top' }}>GPS</td>
                      <td style={{ fontSize: '0.75rem' }}>
                        {previewRecord.exifLatitude.toFixed(6)}, {previewRecord.exifLongitude.toFixed(6)}
                        <a
                          href={`https://www.google.com/maps?q=${previewRecord.exifLatitude},${previewRecord.exifLongitude}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ms-2"
                          style={{ fontSize: '0.7rem' }}
                        >
                          Map
                        </a>
                      </td>
                    </tr>
                  )}
                  {previewRecord.exifDatetime && (
                    <tr>
                      <td style={{ color: '#888', paddingRight: 12, whiteSpace: 'nowrap', verticalAlign: 'top' }}>EXIF Date</td>
                      <td>{new Date(previewRecord.exifDatetime).toLocaleString('ja-JP')}</td>
                    </tr>
                  )}
                  {previewRecord.exifMetadata && (previewRecord.exifMetadata.make || previewRecord.exifMetadata.model) && (
                    <tr>
                      <td style={{ color: '#888', paddingRight: 12, whiteSpace: 'nowrap', verticalAlign: 'top' }}>Camera</td>
                      <td style={{ fontSize: '0.75rem' }}>
                        {[previewRecord.exifMetadata.make, previewRecord.exifMetadata.model].filter(Boolean).join(' ')}
                      </td>
                    </tr>
                  )}
                  {previewRecord.errorMessage && (
                    <tr>
                      <td style={{ color: '#888', paddingRight: 12, whiteSpace: 'nowrap', verticalAlign: 'top' }}>Error</td>
                      <td style={{ color: '#dc3545', fontSize: '0.75rem' }}>{previewRecord.errorMessage}</td>
                    </tr>
                  )}
                  <tr>
                    <td style={{ color: '#888', paddingRight: 12, whiteSpace: 'nowrap', verticalAlign: 'top' }}>ID</td>
                    <td style={{ fontSize: '0.7rem', color: '#888', fontFamily: 'monospace' }}>{previewRecord.id}</td>
                  </tr>
                </tbody>
              </table>

              {/* Position indicator */}
              <div style={{ textAlign: 'right', color: '#888', fontSize: '0.75rem', marginTop: 8 }}>
                {previewIndex + 1} / {previewRecords.length}
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

export default VideoUpload;
